import type {
  ResourceAuthorization,
  ResourceAuthTarget,
  AuthorizationLevel,
} from '../../auth/tenant-auth.service';
import type { TenantAuthorizationResponse } from '../../api/tenant-authorizations.service';
import { memberFullName } from '../store-types';
import { route, type HandlerEntry } from '../handler-registry';

const ALL_TARGETS: readonly ResourceAuthTarget[] = [
  'Tenant',
  'TenantSettings',
  'UserManagement',
  'CoachManagement',
  'Locations',
  'Groups',
  'Enrollments',
  'Classes',
  'Attendance',
  'Events',
  'TenantPayments',
  'StudentManagement',
  'Xp',
  'Chat',
  'Competitions',
  'PublicSite',
  'Announcements',
];

const FULL_LEVELS: readonly AuthorizationLevel[] = ['Read', 'Write', 'Delete'];

const fullAuthorizations: readonly ResourceAuthorization[] = ALL_TARGETS.map((target) => ({
  target,
  levels: FULL_LEVELS,
}));

const chatReadOnlyAuthorizations: readonly ResourceAuthorization[] = [
  { target: 'Chat', levels: ['Read'] },
];

function toAuthResponse(
  member: ReturnType<typeof pick>,
): TenantAuthorizationResponse {
  let authorizations: readonly ResourceAuthorization[];
  if (member.roles.includes('Owner') || member.roles.includes('Admin')) {
    authorizations = fullAuthorizations;
  } else if (member.roles.includes('Coach') || member.roles.includes('Student')) {
    authorizations = chatReadOnlyAuthorizations;
  } else {
    authorizations = [];
  }
  return {
    tenantId: member.tenantId,
    tenantAccountId: member.id,
    email: member.email ?? '',
    roles: member.roles,
    status: member.status === 'Active' ? 'Active' : 'Suspended',
    firstName: member.firstName,
    lastName: member.lastName,
    fullName: memberFullName(member),
    authorizations,
  };
}

function pick(m: {
  id: string;
  email: string | null;
  firstName: string;
  lastName: string;
  roles: readonly import('../../auth/tenant-auth.service').TenantAccountRole[];
  status: import('../../api/tenant-members.service').MemberStatus;
}, tenantId: string) {
  return { ...m, tenantId };
}

export const authorizationsHandlers: readonly HandlerEntry[] = [
  route('GET', /^\/api\/tenants\/([^/]+)\/authorizations$/, (_ctx, data) => {
    return data.members.map((m) => toAuthResponse(pick(m, data.tenant.tenantId)));
  }),
  route('GET', /^\/api\/tenants\/([^/]+)\/authorizations\/([^/]+)$/, (ctx, data) => {
    const accountId = decodeURIComponent(ctx.match[2]);
    const member = data.members.find((m) => m.id === accountId);
    if (!member) return { error: { status: 404, message: 'Not found' } };
    return toAuthResponse(pick(member, data.tenant.tenantId));
  }),
  // Mutations are surfaced as no-ops that echo the current state so forms in the
  // demo don't fail. Real authorization changes would need a much richer handler.
  route('PUT', /^\/api\/tenants\/([^/]+)\/authorizations\/([^/]+)$/, (ctx, data) => {
    const accountId = decodeURIComponent(ctx.match[2]);
    const member = data.members.find((m) => m.id === accountId);
    if (!member) return { error: { status: 404, message: 'Not found' } };
    return toAuthResponse(pick(member, data.tenant.tenantId));
  }),
];
